import { useRef, useState } from 'react';

import { Box, Flex, useOutsideClick } from '@chakra-ui/react';
import { Button, Add as AddIcon } from '@mesh.ai/rabbit';
import { useToast } from 'components/hooks/useToast';

import { AutoResizeInput } from 'uiToolkitV1/AutoResizeInput';
import { TextBodyLarge, TextLabel } from 'uiToolkitV1/Typography';

import { createPOACompetencyInitiative, useGetCompetencyInitiatives } from '../hooks/dataHooks';
import { CompetencyInitiativeLoading } from './CompetencyLoading';

type CompetencyInitiativesListProps = {
  employeeId: string;
  competencyId: string;
  reviewCycleId: string;
  actionPlanId: number;
  toIds: { uuid: string; ownerType: string; metadata: { displayName: string }; profile: { profilePhoto: string } }[];
  onAddInitiative?: () => void;
};

export const CompetencyInitiativesList = ({
  employeeId,
  competencyId,
  reviewCycleId,
  actionPlanId,
  toIds,
  onAddInitiative,
}: CompetencyInitiativesListProps) => {
  const [showAddInitiativeTextField, setShowAddInitiativeTextField] = useState<boolean>(false);
  const [newInitiativeValue, setNewInitiativeValue] = useState<string>('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const toast = useToast();

  const {
    data: initiativesList,
    isLoading: isInitiativesListLoading,
    isError: isInitiativesListError,
    refetch: refetchInitiativesList,
  } = useGetCompetencyInitiatives({
    employeeId,
    competencyId,
  });

  const addInitiative = async () => {
    try {
      await createPOACompetencyInitiative({
        newInitiative: { text: newInitiativeValue, description: '', toIds },
        reviewCycle: reviewCycleId,
        actionPlanId,
        competencyId: Number(competencyId),
      });

      refetchInitiativesList();
      if (onAddInitiative) onAddInitiative();

      toast({
        title: 'Initiative added successfully',
        status: 'success',
      });
    } catch (error) {
      console.error('Error creating initiative : ', error);

      toast({
        title: error?.message || 'An unknown error has occurred, please try again',
        status: 'error',
      });
    }
  };

  const saveInitiative = async () => {
    if (newInitiativeValue.trim() !== '') addInitiative();
    setShowAddInitiativeTextField(false);
    setNewInitiativeValue('');
  };

  useOutsideClick({
    ref: inputRef,
    handler: () => {
      if (showAddInitiativeTextField) saveInitiative();
    },
  });

  if (isInitiativesListLoading) {
    return <CompetencyInitiativeLoading />;
  }

  if (isInitiativesListError) {
    toast({
      title: 'An unknown error has occurred, please try again',
      status: 'error',
    });

    return (
      <Box flex="1" overflow="auto" mx="12px">
        <Flex height="20px" alignItems="center" pl="12px">
          <TextLabel color="text.g2" bold>
            Initiatives:
          </TextLabel>
        </Flex>
      </Box>
    );
  }

  return (
    <Box flex="1" overflow="auto" mx="12px">
      <Flex height="20px" alignItems="center" pl="12px">
        <TextLabel color="text.g2" bold>
          Initiatives:
        </TextLabel>
      </Flex>
      <Box display="flex" flexDirection="column" alignItems="flex-start" gap="12px" alignSelf="stretch">
        {initiativesList.map((initiative) => (
          <Box
            key={initiative.uuid}
            display="flex"
            padding="8px 12px"
            flexDirection="column"
            alignItems="flex-start"
            gap="8px"
            alignSelf="stretch"
            borderBottom="1px solid #EBEFF3"
          >
            <TextBodyLarge fontSize="14px" bold>
              {initiative.text}
            </TextBodyLarge>
          </Box>
        ))}
      </Box>
      <Box>
        {showAddInitiativeTextField && (
          <AutoResizeInput
            ref={inputRef}
            isDashed
            value={newInitiativeValue}
            onChange={(e) => {
              const inputValue = e.target.value.slice(0, 255);
              setNewInitiativeValue(inputValue);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveInitiative();
            }}
          />
        )}
        <Button
          iconLeft={AddIcon}
          variant="text"
          style={{ marginTop: '8px' }}
          onClick={() => {
            setShowAddInitiativeTextField(true);
            inputRef.current?.focus();
          }}
        >
          Add Initiatives
        </Button>
      </Box>
    </Box>
  );
};
